import * as React from 'react';
import classnames from 'classnames';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import kebabCase from 'lodash/kebabCase';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import SVGIcon from 'components/svg-icon/SVGIcon';
import Popover from 'components/popover/Popover';

import { __t, applyTranslations } from 'utils/language';
import { getCountMetric } from 'utils/metrics';
import { getFormattedPercentageRate } from 'utils/formatters';

import './metrics-channels-advanced.scss';

import type { ChannelTypes } from 'types';
import type {
  MetricsChannelsAdvancedItem,
  MetricsChannelsAdvancedLayout,
  MetricsChannelsAdvancedMetricType,
  MetricsChannelsAdvancedType
} from './types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./metrics-channels-advanced.i18n.json'));

type Props = {
  channels: Array<MetricsChannelsAdvancedItem>,
  showChannels?: Array<ChannelTypes>,
  type?: MetricsChannelsAdvancedType,
  layout?: MetricsChannelsAdvancedLayout
};

const FOLLOWERS = 'followers';
const SUBSCRIBERS = 'subscribers';
const ENGAGEMENT_RATE = 'engagementRate';
const YOUTUBE = 'youtube';

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'metrics_channels_advanced';

class MetricsChannelsAdvanced extends React.PureComponent<Props> {
  static defaultProps = {
    type: 'standard',
    layout: 'inline'
  };

  getShouldRenderItemStatus(channelType: ChannelTypes): boolean {
    const { showChannels } = this.props;

    if (isNil(showChannels)) {
      return false;
    }

    return showChannels.includes(channelType);
  }

  renderIcon(item: MetricsChannelsAdvancedItem): React.ReactNode {
    return (
      <SVGIcon
        name={`${item.type}-no-customisation`}
        width={16}
        height={16}
        link={item.url}
      />
    );
  }

  renderIconPopoverBody(item: MetricsChannelsAdvancedItem): React.ReactNode {
    return (
      <div className="metrics-channels-advanced__popover">
        {__t(`${COMMON_MSG_KEY}.basics.open`)}&nbsp;
        {__t(`${COMMON_MSG_KEY}.social.${item.type}`)}
      </div>
    );
  }

  renderMetric(value: string | undefined | null, metricType: MetricsChannelsAdvancedMetricType): React.ReactNode {
    return (
      <div className={`metrics-channels-advanced__metric metrics-channels-advanced__metric--${kebabCase(metricType)}`}>
        {value}
      </div>
    );
  }

  renderMetricPopoverRow = (
    type: 'today' | 'posting_date' | 'offer_agreement_date',
    followers: number | undefined | null,
    engagementRate: number | undefined | null
  ): React.ReactNode => (
    <div className="metrics-channels-advanced__popover-body-row">
      <div className="metrics-channels-advanced__popover-body-row-label">
        {__t(`${MSG_KEY}.labels.${type}`)}
      </div>
      <div className="metrics-channels-advanced__popover-body-row-value">
        {getCountMetric(followers)}
      </div>
      <div className="metrics-channels-advanced__popover-body-row-value">
        {getFormattedPercentageRate(engagementRate, false)}
      </div>
    </div>
  );

  renderMetricPopoverBody = (
    item: MetricsChannelsAdvancedItem,
    metricType: MetricsChannelsAdvancedMetricType
  ): React.ReactNode => (
    <div className="metrics-channels-advanced__popover">
      <div className="metrics-channels-advanced__popover-title">
        {__t(`${COMMON_MSG_KEY}.social.${item.type}`)} {__t(`${MSG_KEY}.labels.channel_data`)}
      </div>
      <div className="metrics-channels-advanced__popover-body">
        <div className="metrics-channels-advanced__popover-body-row">
          <div />
          <div>
            {__t(`${COMMON_MSG_KEY}.kpis.${metricType}`)}
          </div>
          <div>
            {__t(`${COMMON_MSG_KEY}.kpis.engagement_rate`)}
          </div>
        </div>
        {
          (!isNil(item.currentFollowers) || !isNil(item.currentEngagementRate)) &&
            this.renderMetricPopoverRow('today', item.currentFollowers, item.currentEngagementRate)
        }
        {
          (!isNil(item.postDateFollowers) || !isNil(item.postDateEngagementRate)) &&
            this.renderMetricPopoverRow('posting_date', item.postDateFollowers, item.postDateEngagementRate)
        }
        {
          (!isNil(item.offerDateFollowers) || !isNil(item.offerDateEngagementRate)) &&
            this.renderMetricPopoverRow('offer_agreement_date', item.offerDateFollowers, item.offerDateEngagementRate)
        }
      </div>
    </div>
  );

  renderMetrics(item: MetricsChannelsAdvancedItem): React.ReactNode {
    const { type } = this.props;
    const metricType = item.type === YOUTUBE
      ? SUBSCRIBERS
      : FOLLOWERS;

    return (
      <div className="metrics-channels-advanced__metrics">
        {this.renderMetric(getCountMetric(item.currentFollowers), metricType)}
        {
          isNumber(item.currentEngagementRate) && type !== 'limited' &&
            this.renderMetric(getFormattedPercentageRate(item.currentEngagementRate, false), ENGAGEMENT_RATE)
        }
        <div className="metrics-channels-advanced__info-icon">
          <SVGDynamicIcon
            name="info-inside-circle-custom-bg-white-fg"
            width={12}
            height={12}
            customColor="grey"
          />
        </div>
      </div>
    );
  }

  renderChannel = (item: MetricsChannelsAdvancedItem): React.ReactNode | undefined | null => {
    const { layout } = this.props;
    const showItem = this.getShouldRenderItemStatus(item.type);
    const metricType = item.type === YOUTUBE
      ? SUBSCRIBERS
      : FOLLOWERS;

    if (!showItem) {
      return null;
    }

    const channelClasses = classnames('metrics-channels-advanced__channel', {
      [`metrics-channels-advanced__channel--layout-${String(layout)}`]: Boolean(layout)
    });

    return (
      <div
        key={`metrics-channels-advanced__channel--${item.type}-${item.followers}`}
        className={channelClasses}
      >
        <Popover
          triggerComponent={this.renderIcon(item)}
          classNameWrapper="metrics-channels-advanced__popover-wrapper-icon"
        >
          {this.renderIconPopoverBody(item)}
        </Popover>
        <Popover
          triggerComponent={this.renderMetrics(item)}
          classNameWrapper="metrics-channels-advanced__popover-wrapper-metric"
          classNamePopover="metrics-channels-advanced__popover-metric"
        >
          {this.renderMetricPopoverBody(item, metricType)}
        </Popover>
      </div>
    );
  };

  render(): React.ReactNode {
    const { channels, layout } = this.props;

    const containerClasses = classnames('metrics-channels-advanced', {
      [`metrics-channels-advanced--layout-${String(layout)}`]: Boolean(layout)
    });

    return (
      <div className={containerClasses}>
        {channels.map(this.renderChannel)}
      </div>
    );
  }
}

export default MetricsChannelsAdvanced;
